import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Plug, Plus, Trash2, Pencil, Wifi, WifiOff, Loader2,
  Globe, Sparkles, CreditCard, Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: "wordpress", label: "WordPress", icon: Globe },
  { id: "facebook", label: "Facebook", icon: Globe },
  { id: "twitter", label: "Twitter/X", icon: Globe },
  { id: "instagram", label: "Instagram", icon: Globe },
  { id: "openai", label: "OpenAI (GPT)", icon: Sparkles },
  { id: "anthropic", label: "Anthropic (Claude)", icon: Sparkles },
  { id: "google", label: "Google (Gemini)", icon: Sparkles },
  { id: "stripe", label: "Stripe", icon: CreditCard },
  { id: "custom", label: "Custom", icon: Plug },
];

const AUTH_TYPES = ["api_key", "oauth", "basic", "bearer"];

export default function ApiConnectors() {
  const { t } = useLanguage();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [selectedConn, setSelectedConn] = useState(null);
  const [form, setForm] = useState({
    name: "", platform: "custom", api_url: "", api_key: "",
    auth_type: "api_key", capabilities: "", rate_limit: "", cost_per_call: "", status: "active",
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/connectors`);
      setConnectors(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", platform: "custom", api_url: "", api_key: "", auth_type: "api_key", capabilities: "", rate_limit: "", cost_per_call: "", status: "active" });
    setEditing(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        capabilities: form.capabilities ? form.capabilities.split(",").map(c => c.trim()).filter(Boolean) : [],
        rate_limit: form.rate_limit ? parseInt(form.rate_limit) : null,
        cost_per_call: form.cost_per_call || null,
        api_url: form.api_url || null, api_key: form.api_key || null,
      };
      if (editing) {
        await axios.put(`${API}/connectors/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/connectors`, payload);
      }
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false); resetForm();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, platform: c.platform, api_url: c.api_url || "", api_key: c.api_key || "",
      auth_type: c.auth_type, capabilities: (c.capabilities || []).join(", "),
      rate_limit: c.rate_limit ? String(c.rate_limit) : "", cost_per_call: c.cost_per_call || "", status: c.status,
    });
    setDialogOpen(true);
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await axios.post(`${API}/connectors/${id}/test`);
      toast[res.data.success ? "success" : "error"](res.data.message);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setTestingId(null); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/connectors/${id}`);
      if (selectedConn?.id === id) setSelectedConn(null);
      toast.success(t("common.success")); fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="connectors-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("conn.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("conn.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-connector-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("conn.addConnector")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? t("sites.edit") : t("conn.addConnector")}</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.connName")}</Label>
                  <Input data-testid="conn-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.platform")}</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({...form, platform: v})}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("conn.apiUrl")}</Label>
                <Input value={form.api_url} onChange={(e) => setForm({...form, api_url: e.target.value})} placeholder="https://api.example.com" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.apiKey")}</Label>
                  <Input type="password" value={form.api_key} onChange={(e) => setForm({...form, api_key: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.authType")}</Label>
                  <Select value={form.auth_type} onValueChange={(v) => setForm({...form, auth_type: v})}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{AUTH_TYPES.map(a => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("conn.capabilities")}</Label>
                <Input value={form.capabilities} onChange={(e) => setForm({...form, capabilities: e.target.value})} placeholder="posts, chat, images..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.rateLimit")}</Label>
                  <Input type="number" value={form.rate_limit} onChange={(e) => setForm({...form, rate_limit: e.target.value})} placeholder="1000/h" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("conn.costPerCall")}</Label>
                  <Input value={form.cost_per_call} onChange={(e) => setForm({...form, cost_per_call: e.target.value})} placeholder="$0.01" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("sites.cancel")}</Button>
              <Button data-testid="save-conn-btn" onClick={handleSave}>{t("sites.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={selectedConn ? "lg:col-span-2" : "lg:col-span-3"}>
          {connectors.length === 0 ? (
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-0">
                <div data-testid="no-connectors" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Plug className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("conn.noConnectors")}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {connectors.map((c) => {
                const pl = PLATFORMS.find(p => p.id === c.platform);
                const PlIcon = pl?.icon || Plug;
                return (
                  <Card key={c.id} data-testid={`conn-${c.id}`}
                    className={`rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer ${selectedConn?.id === c.id ? "border-primary" : "bg-card"}`}
                    onClick={() => setSelectedConn(c)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <PlIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                          <div>
                            <h3 className="text-sm font-semibold text-foreground">{c.name}</h3>
                            <p className="text-[10px] text-muted-foreground">{pl?.label || c.platform}</p>
                          </div>
                        </div>
                        <div className={`w-2.5 h-2.5 rounded-full ${c.test_result?.success ? "bg-emerald-500" : c.test_result ? "bg-red-500" : "bg-slate-300"}`} />
                      </div>
                      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                        <Badge variant="outline" className="text-[9px] rounded-md">{c.auth_type}</Badge>
                        {(c.capabilities || []).slice(0, 3).map((cap, i) => (
                          <Badge key={i} variant="secondary" className="text-[8px] rounded-md">{cap}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {selectedConn && (
          <div>
            <Card data-testid="conn-detail" className="rounded-xl border shadow-sm bg-card sticky top-20">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-heading text-foreground">{selectedConn.name}</CardTitle>
                  <div className="flex gap-0.5">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(selectedConn)}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(selectedConn.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-1 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <p className="text-[9px] text-muted-foreground">{t("conn.platform")}</p>
                    <p className="text-xs font-medium">{selectedConn.platform}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50">
                    <p className="text-[9px] text-muted-foreground">{t("conn.authType")}</p>
                    <p className="text-xs font-medium">{selectedConn.auth_type}</p>
                  </div>
                </div>
                {selectedConn.api_url && <p className="text-[10px] text-primary truncate">{selectedConn.api_url}</p>}
                {selectedConn.capabilities?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedConn.capabilities.map((c2, i) => <Badge key={i} variant="outline" className="text-[9px] rounded-md">{c2}</Badge>)}
                  </div>
                )}
                {(selectedConn.rate_limit || selectedConn.cost_per_call) && (
                  <div className="flex gap-2 text-[10px] text-muted-foreground">
                    {selectedConn.rate_limit && <span>Rate: {selectedConn.rate_limit}/h</span>}
                    {selectedConn.cost_per_call && <span>Cost: {selectedConn.cost_per_call}</span>}
                  </div>
                )}
                <Separator />
                <Button variant="outline" size="sm" data-testid="test-conn-btn" className="w-full h-8 text-xs gap-1"
                  onClick={() => handleTest(selectedConn.id)} disabled={testingId === selectedConn.id}>
                  {testingId === selectedConn.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                   selectedConn.test_result?.success ? <Wifi className="w-3 h-3 text-emerald-500" /> : <WifiOff className="w-3 h-3" />}
                  {t("conn.testConnection")}
                </Button>
                {selectedConn.test_result && (
                  <p className={`text-[10px] ${selectedConn.test_result.success ? "text-emerald-600" : "text-red-500"}`}>
                    {selectedConn.test_result.message}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
